/** @format */
import {
    MediaQuery,
    desktop,
    largeDesktop,
    largeTablet,
    mobile,
    smallMobile,
    smallTablet,
} from '@mpb-com/feather/helpers/media-queries';
import { css } from 'styled-components';

import { ConstrainedWrapperType, StyledConstrainedWrapperProps } from './index';
import {
    getGridTemplateColumns,
    getMaxWidthSize,
    getPadding,
} from './style-helpers';

const baseStyles = css`
    margin: 0 auto;
`;

/**
 * Type styles for the constrained wrapper component
 *
 * @param {StyledConstrainedWrapperProps} props The constrained wrapper properties object
 * @param {ConstrainedWrapperType} props.type The constrained wrapper type
 * @param {ConstrainedWrapperSize} props.size The constrained wrapper size
 * @param {MediaQuery} props.fullWidth Media query at which we add styles to display full width
 * @param {ConstrainedWrapperGutter} props.gutter The gutter size
 * @param {ReactNode} props.children The children rendered in the wrapper
 * */
const typeStyles = ({
    type,
    size,
    fullWidth,
    gutter,
}: StyledConstrainedWrapperProps) => {
    switch (type) {
        case ConstrainedWrapperType.GRID: {
            /* eslint-disable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
            return css`
                display: grid;

                > * {
                    grid-column: 2;
                }

                ${desktop(css`
                    grid-template-columns: ${getGridTemplateColumns(
                        MediaQuery.DESKTOP,
                        gutter,
                        size
                    )};
                `)}

                ${largeDesktop(css`
                    grid-template-columns: ${getGridTemplateColumns(
                        MediaQuery.LARGE_DESKTOP,
                        gutter,
                        size
                    )};
                `)}

                ${largeTablet(css`
                    grid-template-columns: ${getGridTemplateColumns(
                        MediaQuery.LARGE_TABLET,
                        gutter,
                        size
                    )};
                `)}

                ${smallTablet(css`
                    grid-template-columns: ${getGridTemplateColumns(
                        MediaQuery.SMALL_TABLET,
                        gutter,
                        size
                    )};
                `)}

                ${mobile(css`
                    grid-template-columns: ${getGridTemplateColumns(
                        MediaQuery.MOBILE,
                        gutter,
                        size
                    )};
                `)}

                ${smallMobile(css`
                    grid-template-columns: ${getGridTemplateColumns(
                        MediaQuery.SMALL_MOBILE,
                        gutter,
                        size
                    )};
                `)}

                // Order is important. We need this below the mobile media query
                // If fullWidth media query supplied, we want this to always trump the mobile rule
                @media only screen and ${fullWidth} {
                    grid-template-columns: 0 minmax(0, 1fr) 0;
                }
            `;
            /* eslint-enable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
        }
        case ConstrainedWrapperType.PADDING:
        default:
            /* eslint-disable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
            return css`
                max-width: ${getMaxWidthSize(size)};

                ${desktop(css`
                    padding: ${getPadding(MediaQuery.DESKTOP, gutter)};
                `)}

                ${largeDesktop(css`
                    padding: ${getPadding(MediaQuery.LARGE_DESKTOP, gutter)};
                `)}

                ${largeTablet(css`
                    padding: ${getPadding(MediaQuery.LARGE_TABLET, gutter)};
                `)}

                ${smallTablet(css`
                    padding: ${getPadding(MediaQuery.SMALL_TABLET, gutter)};
                `)}

                ${mobile(css`
                    padding: ${getPadding(MediaQuery.MOBILE, gutter)};
                `)}

                ${smallMobile(css`
                    padding: ${getPadding(MediaQuery.SMALL_MOBILE, gutter)};
                `)}

                // Order is important. We need this below the mobile media query
                // If fullWidth media query supplied, we want this to always trump the mobile rule
                @media only screen and ${fullWidth} {
                    padding: 0;
                    max-width: 100%;
                }
            `;
        /* eslint-enable @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
    }
};

export const styles = (props: StyledConstrainedWrapperProps) => css`
    ${baseStyles}
    ${typeStyles(props)}
`;
